import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { HelpCircle } from 'lucide-react';
import { TranslatedText } from '@/components/TranslatedText';
import { FaqAccordion } from '@/components/ui/faq-chat-accordion';

const faqs = [
  {
    id: 1,
    question: "Is this really possible without showing my face?",
    answer: "Absolutely. Our curriculum covers multiple proven business models that don't require personal branding or being on camera.",
    icon: "❤️",
    iconPosition: "right" as const,
  },
  {
    id: 2,
    question: "Do I need any technical experience?",
    answer: "No prior experience required. We use no-code tools and AI assistants that make implementation accessible to everyone.",
  },
  {
    id: 3,
    question: "How quickly can I see results?",
    answer: "This varies based on your effort. Some members launch within weeks, others take a few months. We provide realistic timelines.",
  },
  {
    id: 4,
    question: "What's in the 7-day free trial?",
    answer: "Access it all for 7 days and pay only if you decide to continue.",
  },
  {
    id: 5,
    question: "What if I'm not satisfied?",
    answer: "We offer a 7-day money-back guarantee. Not happy? Contact us for a full refund, no questions asked.",
  },
  {
    id: 6,
    question: "Do you have referral programs?",
    answer: "Yes we do! Bring some friends and we reward you with an amazing IB structure with no ceilings – that's also a faceless business, isn't it? Referral program available only for members.",
  },
];

export const LightFAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} id="faq" className="py-12 sm:py-20 px-4 bg-white">
      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-blue-50 border border-blue-200 mb-5">
            <HelpCircle className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-700 font-medium">FAQ</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            <TranslatedText>Common Questions</TranslatedText>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <FaqAccordion data={faqs} className="max-w-lg" />
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8 text-center p-5 rounded-xl bg-slate-50 border border-slate-200"
        >
          <p className="text-slate-600 text-sm mb-1">
            <TranslatedText>Still have questions?</TranslatedText>
          </p>
          <a 
            href="mailto:support@facelessaura.club"
            className="text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors"
          >
            support@facelessaura.club
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default LightFAQSection;
